import {
  CompetitionGenderEnum,
  EventCompetitivenessEnum,
  EventPrivacyEnum,
  GameNetStatusesEnum,
  GetListGameRatingOptionsQuery,
  PlaySessionMatchCompetitivenessEnum,
  SportsEnum,
  TeamTypesEnum,
  VenueAccessTypesEnum,
  VenueNetsEnum,
} from 'types/generated/client';
import type { VenueAddress } from 'utils/shared/coachBuilder';
import type { SkillLevelRangeFormValues } from 'components/Find/SkillLevelRangeField';
import type { GameFormat } from 'components/Find/gameFormat';

/**
 * Match type and competitiveness map 1:1 to the DB enums, so the form reuses the
 * generated `TeamTypesEnum` / `EventCompetitivenessEnum` directly. Gender is the one
 * form-only enum — its "All / Men / Women / Nonbinary" options don't line up with the
 * DB `competition_gender` values, so it's mapped to `CompetitionGenderEnum` at submit.
 */
export enum GameGender {
  All = 'ALL',
  Men = 'MEN',
  Women = 'WOMEN',
  Nonbinary = 'NONBINARY',
}

/** A rating system available for the selected sport (a row from a scale table). */
export interface RatingScaleOption {
  id: string;
  shortName: string;
  minimum: number;
  maximum: number;
}

/** A self-rate skill level (from skill_levels), shown as a chip — the generated query row. */
export type SkillLevelOption = GetListGameRatingOptionsQuery['skillLevels'][number];

/** A rating requirement the host adds: which scale + the desired range. */
export interface GameRatingScale {
  scaleId: string;
  shortName: string;
  minimum: number | null;
  maximum: number | null;
}

export interface ListGameFormValues extends SkillLevelRangeFormValues {
  sport: SportsEnum;
  format: GameFormat;
  /** Head count for Open Play (no team composition); null for singles/doubles. */
  participantLimit: number | null;
  /** Open Play only: no head count at all (stored as a null limit). */
  isUnlimitedParticipants: boolean;
  /** Stored directly as `event_groups.gender` (Men's / Women's / Mixed / Co-ed). */
  gender: CompetitionGenderEnum;
  /** Public games appear in the activity feed; private ones are invite-only. */
  privacy: EventPrivacyEnum;
  /** Connection ids invited to the game. */
  invitedPlayerIds: string[];
  /** Native date input value (yyyy-MM-dd). */
  date: string;
  /** Native time input values (HH:mm, 24h). */
  startTime: string;
  endTime: string;
  venueId: string | null;
  isCourtBooked: boolean;
  /** Court cost per player, in major currency units. */
  courtCostPerPlayer: number | null;
  /** "Net provided at venue" toggle; when off, `netFallback` says who brings one. */
  isNetProvidedAtVenue: boolean;
  netFallback: GameNetStatusesEnum;
  description: string;
  saveAsTemplate: boolean;
  templateTitle: string;
}

interface ChipOption<T extends string> {
  label: string;
  value: T;
  /** Colored icon shown inside the chip (greyed when unselected). */
  iconSrc?: string;
}

export const SPORT_OPTIONS: ChipOption<SportsEnum>[] = [
  {
    label: 'Pickleball',
    value: SportsEnum.Pickleball,
    iconSrc: '/images/programming/pickleball.png',
  },
  { label: 'Tennis', value: SportsEnum.Tennis, iconSrc: '/images/programming/tennis.png' },
  { label: 'Padel', value: SportsEnum.Padel, iconSrc: '/images/programming/padel.png' },
];

export const MATCH_TYPE_OPTIONS: ChipOption<TeamTypesEnum>[] = [
  { label: 'Singles', value: TeamTypesEnum.Singles },
  { label: 'Doubles', value: TeamTypesEnum.Doubles },
];

/** The game form's Gender row — the four `competition_gender` values, stored as-is. */
export const GAME_GENDER_OPTIONS: ChipOption<CompetitionGenderEnum>[] = [
  { label: "Men's", value: CompetitionGenderEnum.Male },
  { label: "Women's", value: CompetitionGenderEnum.Female },
  { label: 'Mixed', value: CompetitionGenderEnum.Mixed },
  { label: 'Co-ed', value: CompetitionGenderEnum.Coed },
];

/** Who brings the net when the venue doesn't provide one. */
export const NET_FALLBACK_LABELS: Record<GameNetStatusesEnum, string> = {
  [GameNetStatusesEnum.HostBrings]: 'Bringing net',
  [GameNetStatusesEnum.None]: "Don't have net",
  [GameNetStatusesEnum.VenueProvided]: 'Net provided at venue',
};

export const NET_FALLBACK_OPTIONS: ChipOption<GameNetStatusesEnum>[] = [
  {
    label: NET_FALLBACK_LABELS[GameNetStatusesEnum.HostBrings],
    value: GameNetStatusesEnum.HostBrings,
  },
  { label: NET_FALLBACK_LABELS[GameNetStatusesEnum.None], value: GameNetStatusesEnum.None },
];

export const GENDER_OPTIONS: ChipOption<GameGender>[] = [
  { label: 'All', value: GameGender.All },
  { label: 'Men', value: GameGender.Men },
  { label: 'Women', value: GameGender.Women },
  { label: 'Nonbinary', value: GameGender.Nonbinary },
];

export const COMPETITIVENESS_OPTIONS: ChipOption<EventCompetitivenessEnum>[] = [
  { label: 'Recreational play', value: EventCompetitivenessEnum.Casual },
  { label: 'Competitive', value: EventCompetitivenessEnum.Competitive },
];

/** The badge (`ActivityBadge`) is typed to the play-session enum, so map the game's
 *  competitiveness across ("Competitive" → Match). */
export const COMPETITIVENESS_TO_ENUM: Record<
  EventCompetitivenessEnum,
  PlaySessionMatchCompetitivenessEnum
> = {
  [EventCompetitivenessEnum.Casual]: PlaySessionMatchCompetitivenessEnum.Casual,
  [EventCompetitivenessEnum.Competitive]: PlaySessionMatchCompetitivenessEnum.Match,
  // Availability-only game types; the badge has no face for them yet.
  [EventCompetitivenessEnum.Any]: PlaySessionMatchCompetitivenessEnum.Casual,
  [EventCompetitivenessEnum.MatchPrep]: PlaySessionMatchCompetitivenessEnum.Match,
  [EventCompetitivenessEnum.Drill]: PlaySessionMatchCompetitivenessEnum.Casual,
};

/** The gender summary line in the game preview's Description section. */
export const GAME_GENDER_SUMMARY_LABELS: Record<CompetitionGenderEnum, string> = {
  [CompetitionGenderEnum.Male]: 'Men only',
  [CompetitionGenderEnum.Female]: 'Women only',
  [CompetitionGenderEnum.Mixed]: 'Mixed',
  [CompetitionGenderEnum.Coed]: 'All players',
};

/** The gender summary line in the preview's Description section. */
export const GENDER_SUMMARY_LABELS: Record<GameGender, string> = {
  [GameGender.All]: 'All players',
  [GameGender.Men]: 'Men',
  [GameGender.Women]: 'Women',
  [GameGender.Nonbinary]: 'Nonbinary',
};

/** VenueAddress plus a "Brooklyn, New York" label for preview pin lines. */
export type VenueAddressWithCity = VenueAddress & {
  cityLabel?: string | null;
};

/** Venue details for the preview's Location section. */
export interface PreviewVenue {
  title: string;
  addressString: string;
  imageUrl?: string;
  accessType?: VenueAccessTypesEnum | null;
  totalCourtCount?: number;
  pickleballNets?: VenueNetsEnum | null;
  /** "Brooklyn, New York" — shown on the pin line when the design calls for it. */
  cityLabel?: string | null;
}

export interface PreviewStepProps {
  values: ListGameFormValues;
  venue?: PreviewVenue | null;
  onGoBack: () => void;
  onPublish: () => void;
  isPublishing?: boolean;
  /** "DUPR 3 - 5" / "Intermediate - Advanced" — the title's rating half. */
  skillSummary?: string;
  /** The final CTA label — "Publish" when creating, "Save changes" when editing. */
  publishLabel?: string;
}

export interface LocationFieldProps {
  /** The selected venue id (form value). */
  value: string | null;
  onChange: (venueId: string | null) => void;
  /** Full details of the selected venue — the preview step needs more than the id. */
  onVenueSelected?: (venue: VenueAddress | null) => void;
  /**
   * Venues to seed the slider with (edit mode) so a currently-selected venue that
   * isn't in the nearby geo results still shows and stays highlighted.
   */
  initialVenues?: VenueAddress[];
}
